/* eslint-disable no-console */
import { getCookie } from 'widgets/toolbox/cookie';
import sessionStorageWrapper from 'core/toolbox/sessionStorageWrapper';

type TOptimizelyDecision = {
    variationKey: string | null;
};

type TOptimizelyDecisionEvent = {
    decisionInfo: TOptimizelyDecision;
};

type TOptimizelyEventTags = Record<string, unknown>;

interface IOptimizelyUserContext {
    decide(key: string): TOptimizelyDecision;
    trackEvent(eventKey: string, eventTags?: TOptimizelyEventTags): void;
}

interface IOptimizelyClient {
    onReady(): Promise<void>;
    createUserContext(userId: string): IOptimizelyUserContext;
    notificationCenter: {
        addNotificationListener(args1: string, args2: object): object;
    };
}

type TPollingProjectConfigManagerOptions = {
    sdkKey: string;
    autoUpdate: boolean;
    updateInterval: number;
};

type TBatchEventProcessorOptions = {
    eventBatchSize: number;
    eventFlushInterval: number;
};

type TOdpManagerOptions = Record<string, never>;

interface IOptimizelyInstanceOptions {
    projectConfigManager: TPollingProjectConfigManagerOptions;
    eventProcessor: TBatchEventProcessorOptions;
    odpManager: TOdpManagerOptions;
}

interface IOptimizelySdk {
    NOTIFICATION_TYPES: {
        DECISION: string;
    };
    createInstance(args: unknown): IOptimizelyClient | undefined;
    createPollingProjectConfigManager(projectConfigManagerOptions: TPollingProjectConfigManagerOptions): unknown;
    createBatchEventProcessor(eventProcessorOptions: TBatchEventProcessorOptions): unknown;
    createOdpManager(odpManagerOptions: TOdpManagerOptions): unknown;
}

declare global {
    interface Window {
        optimizelySdk: IOptimizelySdk;
        _uxa: Array<object> | null;
    }
}

/**
 * @class OptimizelyFeatureExperimentationInstance
 * @description Creates and exposes singleton for Optimizely Feature Experimentation SDK.
 * A singleton is encapsulating the many possible calls to create the 'instance' and 'user' objects
 * a page might have, depending on how many experiments it runs.
 * @param sdkKey - The Optimizely SDK key used to initialize the SDK instance.
 * @param customerId - The customer/user ID to associate with the Optimizely user context.
 */

class OptimizelyFeatureExperimentationInstance {
    private user?: IOptimizelyUserContext;

    private sdkKey: string = '';

    private readonly customerId?: string | null;

    private readonly instanceOptions!: IOptimizelyInstanceOptions;

    private loadingPromise?: Promise<IOptimizelyClient | null>;

    constructor() {
        try {
            const { API_KEY, customerId } = this.getInstanceData();

            if (!API_KEY) {
                return;
            }

            this.sdkKey = API_KEY;
            this.customerId = customerId;
            this.instanceOptions = this.generateInstanceOptions();

            this.initSDK();
        } catch (error) {
            console.log(error);
        }
    }

    /**
     * Retrieve API key, customer ID from featureExperimentationData meta tag's content.
     */
    private getInstanceData(): { API_KEY: string | null; customerId: string | null } {
        const featureExperimentationInstanceDataJSON = document.querySelector('meta[name="featureExperimentationData"]')?.getAttribute('content');

        if (!featureExperimentationInstanceDataJSON) {
            return { API_KEY: null, customerId: null };
        }

        const featureExperimentationInstanceData = JSON.parse(featureExperimentationInstanceDataJSON);

        return featureExperimentationInstanceData;
    }

    /**
     * Generates instance options object from Site Preferences or
     * returns a default configuration if preferences are unavailable.
     */
    private generateInstanceOptions(): IOptimizelyInstanceOptions {
        const customInstanceOptionsJSON = document.querySelector('meta[name="featureExperimentationInstanceOptions"]')?.getAttribute('content');

        let instanceOptions: IOptimizelyInstanceOptions = {
            projectConfigManager: {
                sdkKey: this.sdkKey,
                autoUpdate: true,
                updateInterval: 900000
            },
            eventProcessor: {
                eventBatchSize: 10,
                eventFlushInterval: 3000
            },
            odpManager: {}
        };

        try {
            const customInstanceOptions = JSON.parse(customInstanceOptionsJSON || '{}') as IOptimizelyInstanceOptions;

            if (customInstanceOptions && typeof customInstanceOptions === 'object' && Object.keys(customInstanceOptions).length) {
                instanceOptions = customInstanceOptions;
            }
        } catch (e) {
            console.error(`Unable to generate optimizely experimentation instance from custom preferences.
                Defaulting to hardcoded settings. Error: ${e}`);
        }

        return instanceOptions;
    }

    /**
     * Attempts to instantiate Optimizely user context and return its loading promise.
     * If unavailable, return instantly resolved promise.
     *
     * @returns Promise<IOptimizelyClient | null>
     */
    private async initSDK(): Promise<IOptimizelyClient | null> {
        if (this.loadingPromise) {
            return this.loadingPromise;
        }

        if (!window.optimizelySdk) {
            return new Promise(resolve => {
                window.addEventListener(
                    'load',
                    async () => {
                        resolve(await this.initSDK());
                    },
                    { once: true }
                );
            });
        }

        if (!this.sdkKey || !window.optimizelySdk) {
            console.warn('Optimizely SDK is not available on the window object.');

            return null;
        }

        const userId = this.getUserID();
        const optimizelySdk = window.optimizelySdk;

        /**
         * Fetches the datafile (object containing project configuration) at intervals
         */
        const pollingConfigManager = optimizelySdk.createPollingProjectConfigManager(this.instanceOptions.projectConfigManager);

        // BatchEventProcessor queues up events, send them as batches. Reduces network calls.
        const batchEventProcessor = optimizelySdk.createBatchEventProcessor(this.instanceOptions.eventProcessor);

        // ODPManager is necessary to use real time audiences, if so desired.
        const odpManager = optimizelySdk.createOdpManager(this.instanceOptions.odpManager);

        // Notification types, e.g. 'DECISION'
        const NOTIFICATION_TYPES = optimizelySdk.NOTIFICATION_TYPES;

        const optimizelyClient = optimizelySdk.createInstance({
            projectConfigManager: pollingConfigManager,
            eventProcessor: batchEventProcessor,
            odpManager
        });

        if (!optimizelyClient) {
            console.error('Unable to instantiate Optimizely Client SDK.');

            return null;
        }

        optimizelyClient.notificationCenter.addNotificationListener(NOTIFICATION_TYPES.DECISION, this.dispatchDecisionEvent);

        this.loadingPromise = optimizelyClient
            .onReady()
            .then(() => {
                this.user = optimizelyClient.createUserContext(userId);

                return optimizelyClient;
            })
            .catch(e => {
                console.error(e);

                return null;
            });

        return this.loadingPromise;
    }

    /**
     * Tries to retrieve user ID from either BE data or sessionStorage.
     * Calls to generate a new one if unavailable.
     *
     * @returns userId
     */
    private getUserID(): string {
        let userId: string;

        const testUserCookie = getCookie('optimizely_test_user');

        if (testUserCookie && testUserCookie.length) {
            return testUserCookie;
        }

        if (this.customerId) {
            userId = this.customerId;
        } else if (sessionStorageWrapper.getItem('optiUserId')) {
            userId = sessionStorageWrapper.getItem('optiUserId') as string;
        } else {
            userId = this.generateID();
            sessionStorageWrapper.setItem('optiUserId', userId);
        }

        return userId;
    }

    /**
     * Generates a unique user ID using timestamp and random string
     *
     * @returns userId - string
     */
    private generateID(): string {
        const timestamp = Date.now();
        const randomID = crypto.randomUUID();

        return `${timestamp}_${randomID}`;
    }

    /**
     * Retrieves the Optimizely Feature Experimentation SDK user context
     *
     * @returns Promise<IOptimizelyUserContext | undefined>
     */
    private async getUserContext(): Promise<IOptimizelyUserContext | undefined> {
        if (!this.user && this.sdkKey) {
            await this.initSDK();
        }

        return this.user;
    }

    /**
     * Dispatches decision for analytics tracking after gtm loading.
     *
     * @param decisionEvent - TOptimizelyDecisionEvent
     */
    private dispatchDecisionEvent(decisionEvent: TOptimizelyDecisionEvent): void {
        window.dispatchEvent(new CustomEvent('optimizely_decision_info', { detail: decisionEvent.decisionInfo }));
    }

    /**
     * Calls SDK's 'track' API.
     *
     * @param eventLabel - label of event to track.
     * @param data - additional tracking data.
     */
    async trackEvent(eventLabel: string, data?: TOptimizelyEventTags) {
        await this.getUserContext();

        this.user?.trackEvent(eventLabel, data);
    }

    /**
     * Calls SDK's 'decide' API.
     *
     * @param flag - flag to decide the behavior on.
     * @returns Promise<string>
     */
    async decide(flag: string): Promise<string> {
        await this.getUserContext();

        const decision = this.user?.decide(flag);

        return decision?.variationKey || 'off';
    }
}

let instance: OptimizelyFeatureExperimentationInstance;

/**
 * Instantiates OptimizelyFeatureExperimentationInstance and exposes cached instance.
 * If instance is undefined, call constructor. Else, return instance.
 */
export const getFeatureExperimentationInstance = (): OptimizelyFeatureExperimentationInstance => {
    // if instantiated, return immediately
    if (!instance) {
        instance = new OptimizelyFeatureExperimentationInstance();
    }

    return instance;
};

export type TOptimizelyFeatureExperimentationInstance = InstanceType<typeof OptimizelyFeatureExperimentationInstance>;
